/**
 * GameControlButton renders an icon button for game controls in the header.
 *
 * Why: The info, configure, and close buttons share the same styling pattern
 * with hover effects and focus ring. Extracting this avoids repetition and
 * keeps GameControls focused on layout.
 *
 * When to use: Used by GameControls for the icon buttons (info, configure, close).
 * Must use forwardRef: CloseConfirmDialog passes one of these as a
 * FocusNavigationDialog `trigger`, which Radix's Dialog.Trigger asChild
 * clones and attaches its own ref to — a plain function component can't
 * accept that ref (see RoomMenuTrigger for the same pattern).
 */
import * as React from 'react'
import { cn } from '@merels/ui'
import { IconButton } from '../../library/IconButton/IconButton'

export interface GameControlButtonProps {
  /** Ref for focus management (callback or object ref) */
  buttonRef?: React.Ref<HTMLButtonElement>
  /** The icon to display */
  icon: React.ReactNode
  /** Click handler */
  onClick?: () => void
  /** Accessible label */
  ariaLabel: string
  /** Tooltip text */
  title: string
  /** Test ID for E2E tests */
  testId?: string
  /** Whether this button is currently focused */
  isFocused?: boolean
  /** Accent color for styling */
  accentColor?: string
  /** Ink color when the control is filled with accentColor (hover) */
  accentInkColor?: string
  /** Background color for focus ring */
  backgroundColor?: string
}

/** Merge multiple refs into one callback ref */
function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T | null>).current = value
      }
    })
  }
}

export const GameControlButton = React.forwardRef<HTMLButtonElement, GameControlButtonProps>(({
  buttonRef,
  icon,
  onClick,
  ariaLabel,
  title,
  testId,
  isFocused,
  accentColor,
  accentInkColor = '#ffffff',
  backgroundColor,
}, ref) => {
  return (
    <IconButton
      ref={mergeRefs(ref, buttonRef)}
      className={cn(
        "inline-flex items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus",
        isFocused && "outline-none"
      )}
      style={{
        width: 'var(--menu-btn-size,36px)',
        height: 'var(--menu-btn-size,36px)',
        ...(isFocused && accentColor ? {
          boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
        } : {}),
        ...(accentColor ? { color: accentColor } : {}),
      }}
      tabIndex={isFocused ? 0 : -1}
      aria-label={ariaLabel}
      title={title}
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (accentColor) {
          e.currentTarget.style.backgroundColor = accentColor
          e.currentTarget.style.color = accentInkColor
        }
      }}
      onMouseLeave={(e) => {
        if (accentColor) {
          e.currentTarget.style.backgroundColor = 'transparent'
          e.currentTarget.style.color = accentColor
        }
      }}
      icon={icon}
    />
  )
})
GameControlButton.displayName = 'GameControlButton'
